import React from 'react';
import PageHelmet from '../../../components/shared/PageHelmet';
import NavHeader from '../../../components/NavHeader/NavHeader';
import MainFooter from '../../../components/MainFooter/MainFooter';
import IndustryHero from '../../../components/IndustryHero/IndustryHero';
import IndustryChallenge from '../../../components/IndustryChallenge/IndustryChallenge';
import IndustryProductSpotlight from '../../../components/IndustryProductSpotlight/IndustryProductSpotlight';
import IndustryTechStack from '../../../components/IndustryTechStack/IndustryTechStack';
import OurExpertise from '../../../components/OurExpertise/OurExpertise';
import KeyFeatures from '../../Offerings/KeyFeatures/KeyFeatures';
import CommonCtaArea from '../../../components/CommonCtaArea/CommonCtaArea';
import { Landmark, Users, Smartphone, LayoutDashboard, Sparkles, Terminal, LayoutGrid, Database, PaintBucket, CreditCard, Briefcase, FileText, Cog, BarChart3 } from 'lucide-react';

const ChallengeItems = [
   'Complex workflows',
   'Large volumes of data',
   'Secure user experiences',
   'Digital customer onboarding',
   'Financial transactions',
   'Reporting and analytics',
   'Integrations with external systems',
   'Scalable applications',
];

const WhatWeBuildData = [
   { id: 1, icon: <Landmark size="64" strokeWidth='1' />, title: 'Financial Platforms', content: 'Custom platforms for financial-service workflows and business operations.' },
   { id: 2, icon: <Users size="64" strokeWidth='1' />, title: 'Customer Portals', content: 'Digital portals that simplify customer interactions and service management.' },
   { id: 3, icon: <Smartphone size="64" strokeWidth='1' />, title: 'Mobile Financial Applications', content: 'Modern mobile experiences for customers, professionals, and business teams.' },
   { id: 4, icon: <LayoutDashboard size="64" strokeWidth='1' />, title: 'Business Dashboards', content: 'Real-time dashboards for monitoring operations, users, transactions, and business performance.' },
   { id: 5, icon: <Sparkles size="64" strokeWidth='1' />, title: 'AI-Powered Financial Workflows', content: 'Use AI to automate document processing, information extraction, classification, and repetitive workflows.' },
];

const TechData = [
   { icon: <Terminal size={32} strokeWidth={1.5} />, name: 'Python', descriptor: 'Backend systems and APIs' },
   { icon: <LayoutGrid size={32} strokeWidth={1.5} />, name: 'React & Angular', descriptor: 'Web applications and dashboards' },
   { icon: <Smartphone size={32} strokeWidth={1.5} />, name: 'React Native & Flutter', descriptor: 'Mobile applications' },
   { icon: <Database size={32} strokeWidth={1.5} />, name: 'SQL & PostgreSQL', descriptor: 'Reliable data management' },
   { icon: <Sparkles size={32} strokeWidth={1.5} />, name: 'AI', descriptor: 'Intelligent automation and document processing' },
   { icon: <PaintBucket size={32} strokeWidth={1.5} />, name: 'UI/UX', descriptor: 'Simple and intuitive financial experiences' },
];

const SolutionsData = [
   { id: 1, icon: <Landmark size="42" strokeWidth='1' />, title: 'Tax Platforms', content: '' },
   { id: 2, icon: <LayoutDashboard size="42" strokeWidth='1' />, title: 'Financial Dashboards', content: '' },
   { id: 3, icon: <CreditCard size="42" strokeWidth='1' />, title: 'Payment-related Applications', content: '' },
   { id: 4, icon: <Users size="42" strokeWidth='1' />, title: 'Customer Portals', content: '' },
   { id: 5, icon: <Briefcase size="42" strokeWidth='1' />, title: 'Professional Service Platforms', content: '' },
   { id: 6, icon: <FileText size="42" strokeWidth='1' />, title: 'Document Processing Systems', content: '' },
   { id: 7, icon: <Cog size="42" strokeWidth='1' />, title: 'Financial Workflow Automation', content: '' },
   { id: 8, icon: <BarChart3 size="42" strokeWidth='1' />, title: 'Reporting & Analytics Platforms', content: '' },
];

const FinTech = () => {
   return (
      <>
         <PageHelmet
            pageTitle="FinTech Software Solutions"
            description="Technology solutions designed to simplify financial workflows, improve customer experiences, and build secure, scalable digital platforms for FinTech and financial-service businesses."
            keywords="FinTech Software Development, Financial Platform Development, Harsoft Solutions"
            path="/industries/fintech"
         />

         <NavHeader />
         <main>
            <IndustryHero
               title="Build Smarter Digital Solutions for FinTech"
               intro="Technology solutions designed to simplify financial workflows, improve customer experiences, and build secure, scalable digital platforms."
               description="Harsoft helps FinTech and financial-service businesses transform complex processes into intuitive digital experiences using modern web, mobile, backend, database, and AI technologies."
               primaryCta={{ label: 'Build Your FinTech Solution', to: '/contact' }}
               secondaryCta={{ label: 'Talk to Our Experts', to: '/contact' }}
            />

            <IndustryChallenge
               title="The FinTech Challenge"
               intro="Financial businesses need technology that can handle:"
               items={ChallengeItems}
               outro="Harsoft helps businesses turn these challenges into practical digital solutions."
            />

            <OurExpertise
               title="What We Build"
               subtitle="Solutions Built for Financial Services"
               content="From customer-facing portals to internal financial systems, we build the platforms FinTech businesses run on."
               OurExpertiseData={WhatWeBuildData}
            />

            <IndustryTechStack items={TechData} />

            <IndustryProductSpotlight
               name="MyTaxBid"
               tagline="A Digital Platform for Tax & Professional Services"
               description="MyTaxBid connects taxpayers with tax professionals through a structured digital experience for discovering, engaging, and managing tax services."
               ctaLabel="Explore MyTaxBid"
               ctaLink="/contact"
            />

            <KeyFeatures KeyFeaturesData={SolutionsData} title="FinTech Solutions We Can Build" />

            <CommonCtaArea
               title={<>Have a FinTech Idea?</>}
               subtitle="Let's turn your financial-service challenge into a scalable digital product."
               buttonText="Start Your FinTech Project"
               buttonLink="/contact"
            />
         </main>
         <MainFooter />
      </>
   );
};

export default FinTech;
